import { Fragment } from 'react';

import { Document, Font, Page, View } from '@react-pdf/renderer';
import { HyphenationCallback } from '@react-pdf/types';
import { DateTime } from 'luxon';

import { commonStyles } from 'src/common/pdf/common.styles';

import { InvoiceColumnKey } from '../../modals/DownloadInvoices/steps/components/InvoiceColumnSelect';
import { InvoiceExportCondensedReportItem } from '../../types';
import { SimplePaymentsLine } from '../components/payments/SimplePaymentsLine';
import { useInvoiceData } from '../useInvoiceData';
import { InvoicePDFFooter } from './InvoicePDFFooter';
import { InvoicePDFHeader } from './InvoicePDFHeader';
import { InvoicePDFOrganizationHeader } from './InvoicePDFOrganizationHero';
import { InvoicePDFPriceSummary } from './InvoicePDFPriceSummary';
import { InvoicePDFStats } from './InvoicePDFStats';
import { InvoicePDFTripModifierTable } from './InvoicePDFTripModifierTable';
import { InvoicePDFTripTable } from './InvoicePDFTripTable';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

// Support for emojis, see: https://react-pdf.org/fonts
Font.registerEmojiSource({
  format: 'png',
  url: 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/',
});

// Avoid hyphenations
const hyphenationCallback: HyphenationCallback = (word) => [word];
Font.registerHyphenationCallback(hyphenationCallback);

interface InvoicePDFDocumentProps {
  organizationBillingAddress: string;
  organizationBillingEmail: string;
  organizationBillingPhone: string;
  organizationName: string;
  organizationLogo: string;
  organizationTaxId?: string;
  invoiceNote: string;
  invoiceItems: InvoiceExportCondensedReportItem[];
  includePriceSummary?: boolean;
  selectedInvoiceColumns: SelectOption<InvoiceColumnKey>[];
  includePastDueLabel?: boolean;
}

export function InvoicePDFDocument({
  organizationBillingAddress,
  organizationBillingEmail,
  organizationBillingPhone,
  organizationLogo,
  organizationName,
  organizationTaxId,
  invoiceItems,
  invoiceNote,
  includePriceSummary = false,
  selectedInvoiceColumns,
  includePastDueLabel = true,
}: InvoicePDFDocumentProps) {
  const updatedInvoiceItems = useInvoiceData(invoiceItems);

  return (
    <Document>
      {updatedInvoiceItems.map((ii) => {
        return (
          <Fragment key={ii.item.InvoiceNo}>
            <Page size="A4" orientation="landscape">
              <View style={[commonStyles.page, { paddingTop: 0 }]}>
                <InvoicePDFHeader
                  invoiceNumber={ii.item.InvoiceNo}
                  issueDate={ii.invoiceDate.toLocaleString(DateTime.DATE_SHORT)}
                  amountDue={ii.invoiceDueAmount}
                  billingAddress={organizationBillingAddress}
                  billingEmail={organizationBillingEmail}
                  billingPhone={organizationBillingPhone}
                  organizationName={organizationName}
                  logo={organizationLogo}
                />
                <InvoicePDFOrganizationHeader
                  organizationName={organizationName}
                  invoiceNote={invoiceNote}
                />
                <InvoicePDFStats
                  amountDue={ii.invoiceDueAmount}
                  dueDate={ii.invoiceDueDate}
                  miles={ii.totalMiles}
                  passengerCount={ii.totalUniqPassengers}
                  payerAddress={ii.item.ItemPayerBillingAddress}
                  payerName={
                    ii.item.ItemPayerAlternateName || ii.item.ItemPayerName
                  }
                  payerContactName={ii.item.ItemPayerBillingContactName}
                  payerPhone={ii.item.ItemPayerBillingContactPhone}
                  tripCount={ii.totalTrips}
                  organizationTaxId={organizationTaxId}
                  includePastDueLabel={includePastDueLabel}
                />
                <InvoicePDFTripTable
                  rows={ii.item.items}
                  selectedInvoiceColumns={selectedInvoiceColumns}
                />
                <InvoicePDFFooter
                  adjustmentAmount={ii.modifiersTotalAmount}
                  invoiceNotes={ii.item.InvoiceNotes}
                  subTotalAmount={ii.tripsTotalAmount}
                  totalAmount={ii.invoiceDueAmount}
                  paymentLine={
                    ii.paymentsTotalAmount ? (
                      <SimplePaymentsLine
                        paymentAmount={ii.paymentsTotalAmount}
                      />
                    ) : null
                  }
                />
              </View>
            </Page>
            {includePriceSummary &&
            (ii.priceSummaryCompatibleLineItems.length ||
              ii.modifierLineItems.length) ? (
              <Page size="A4" orientation="landscape">
                <View style={[commonStyles.page, { paddingTop: 0 }]}>
                  <InvoicePDFHeader
                    invoiceNumber={ii.item.InvoiceNo}
                    issueDate={ii.invoiceDate.toLocaleString(
                      DateTime.DATE_SHORT
                    )}
                    amountDue={ii.invoiceDueAmount}
                    billingAddress={organizationBillingAddress}
                    billingEmail={organizationBillingEmail}
                    billingPhone={organizationBillingPhone}
                    organizationName={organizationName}
                    logo={organizationLogo}
                  />
                  <InvoicePDFPriceSummary
                    invoiceNumber={ii.item.InvoiceNo}
                    items={ii.priceSummaryCompatibleLineItems}
                  />
                  <InvoicePDFTripModifierTable rows={ii.modifierLineItems} />
                </View>
              </Page>
            ) : null}
          </Fragment>
        );
      })}
    </Document>
  );
}
